import React from 'react';
import { motion } from 'framer-motion';
import { Zap } from 'lucide-react';

interface BatteryIconProps {
  percentage: number;
  isCharging: boolean;
  className?: string;
}

export const BatteryIcon: React.FC<BatteryIconProps> = ({ percentage, isCharging, className = "" }) => {
  // Flat colors, no gradients
  const getColor = () => {
    if (percentage <= 20) return "bg-[#FF3B30]"; // Danger Red
    if (percentage <= 50) return "bg-yellow-600"; // Muted Yellow
    return "bg-[#32D74B]"; // Muted Green
  };

  return (
    <div className={`flex flex-col items-center gap-2 ${className}`}>
      {/* Battery Container - Minimal Border */}
      <div className="relative">
        <div className="w-[140px] md:w-[180px] 3xl:w-[220px] 4xl:w-[260px] 5xl:w-[300px] h-[50px] md:h-[60px] 3xl:h-[70px] 4xl:h-[80px] 5xl:h-[90px] border border-white/20 rounded-xl p-1 md:p-1.5 3xl:p-2 4xl:p-2.5 5xl:p-3 flex items-center bg-[#0a0a0a]">
           
           {/* Inner Fill - Solid, Flat */}
          <motion.div 
            className={`h-full ${getColor()} rounded-lg relative flex items-center justify-center overflow-hidden`}
            initial={{ width: "5%" }}
            animate={{ width: `${Math.max(5, percentage)}%` }}
            transition={{ type: "spring", stiffness: 45, damping: 25 }}
          >
             {/* Percentage Text inside the bar for contrast if bar is wide enough, 
                 but for minimalism, let's keep it centered in the container using absolute positioning below */}
          </motion.div>

          {/* Absolute Text Overlay - Centered in the battery container */}
          <div className="absolute inset-0 flex items-center justify-center z-10">
             <div className="flex items-center gap-1 mix-blend-difference text-white">
                {/* Charging indicator - white lightning on the left */}
                {isCharging && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    className="absolute -left-8 md:-left-10 3xl:-left-12 4xl:-left-14 5xl:-left-16"
                  >
                    <Zap className="w-4 h-4 md:w-5 md:h-5 3xl:w-6 3xl:h-6 4xl:w-7 4xl:h-7 5xl:w-8 5xl:h-8 text-white fill-white" />
                  </motion.div>
                )}
                
                <span className="font-mono font-medium tracking-tight text-base md:text-lg 3xl:text-xl 4xl:text-2xl 5xl:text-3xl">
                    {Math.round(percentage)}%
                </span>
             </div>
          </div>

        </div>
        
        {/* Battery Nipple - Flat */}
        <div className="absolute -right-2 md:-right-2.5 3xl:-right-3 4xl:-right-3.5 5xl:-right-4 top-1/2 -translate-y-1/2 w-2 md:w-2.5 3xl:w-3 4xl:w-3.5 5xl:w-4 h-6 md:h-8 3xl:h-10 4xl:h-12 5xl:h-14 bg-white/20 rounded-r-sm" />
      </div>
    </div>
  );
};